// src/components/UserManagement.js
import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { db } from '../utils/firebaseConfig';
import { logUserActivity } from '../utils/auditLogger';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { FiUserX, FiUserCheck, FiSave, FiSearch, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import ConfirmationModal from './ConfirmationModal';
import { presenceTracker } from '../utils/presenceTracker';
import { auth } from '../utils/firebaseConfig';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const { currentUser, userName } = useContext(AuthContext);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const usersPerPage = 10;
  const modalRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;

    fetchUsers();
    
    let cleanup = () => {};
    const initializePresence = async () => {
      try {
        // Start tracking presence when component mounts
        await presenceTracker.startTracking();
        
        // Subscribe to online users only if still authenticated
        if (auth.currentUser) {
          const unsubscribeFunc = presenceTracker.subscribeToOnlineUsers((onlineUsersList) => {
            setOnlineUsers(onlineUsersList);
          });
          
          // Store the cleanup function
          cleanup = typeof unsubscribeFunc === 'function' ? unsubscribeFunc : () => {};
        }
      } catch (error) {
        console.error('Error initializing presence:', error);
      }
    };

    initializePresence();

    return () => {
      try {
        cleanup();
      } catch (error) {
        console.error('Error cleaning up online users subscription:', error);
      }
    };
  }, [currentUser]);

  // Function to check if a user is online
  const isUserOnline = (userId) => {
    return onlineUsers.includes(userId);
  };

  // Function to render user status
  const UserStatus = ({ userId }) => {
    const online = isUserOnline(userId);
    return (
      <span className={`px-1.5 py-0.5 text-[10px] flex items-center ${
        online 
          ? 'text-green-600 dark:text-green-400' 
          : 'text-gray-500 dark:text-gray-400'
      } font-medium`}>
        <span className={`w-1.5 h-1.5 rounded-full mr-1 ${
          online 
            ? 'bg-green-500 animate-pulse' 
            : 'bg-gray-400'
        }`}></span>
        {online ? 'Online' : 'Offline'}
      </span>
    );
  };

  useEffect(() => {
    // Reset to first page when search/filter changes
    setCurrentPage(1);
  }, [searchTerm, filterRole]);

  const fetchUsers = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let result = [...users];
    
    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.idNumber?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply role filter
    if (filterRole !== 'all') {
      result = result.filter(user => user.role === filterRole);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [users, searchTerm, filterRole, sortField, sortDirection]);

  // Get current users for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredAndSortedUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredAndSortedUsers.length / usersPerPage);

  const openConfirmModal = (title, message, onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm
    });
    // Ensure modal is visible after it opens with a longer timeout
    setTimeout(() => {
      if (modalRef.current) {
        const viewportHeight = window.innerHeight;
        const modalRect = modalRef.current.getBoundingClientRect();
        
        if (modalRect.top < 0 || modalRect.bottom > viewportHeight) {
          window.scrollTo({
            top: modalRect.top + window.scrollY - (viewportHeight - modalRect.height) / 2,
            behavior: 'smooth'
          });
        }
      }
    }, 300);
  };

  const handleBulkAction = async (action) => {
    if (actionInProgress || !selectedUsers.length) return;
    
    // Filter out current user from selection if trying to deactivate
    const usersToUpdate = action === 'deactivate' 
      ? selectedUsers.filter(id => id !== currentUser.uid)
      : selectedUsers;

    // If all selected users were filtered out (only tried to deactivate self)
    if (action === 'deactivate' && usersToUpdate.length === 0) {
      toast.error('Cannot deactivate your own account');
      return;
    }
    
    openConfirmModal(
      `${action.charAt(0).toUpperCase() + action.slice(1)} Users`,
      `Are you sure you want to ${action} ${usersToUpdate.length} selected user${usersToUpdate.length > 1 ? 's' : ''}?`,
      async () => {
        setActionInProgress(true);
        try {
          for (const userId of usersToUpdate) {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, { active: action === 'activate' });
          }

          setUsers(prevUsers =>
            prevUsers.map(user =>
              usersToUpdate.includes(user.id)
                ? { ...user, active: action === 'activate' }
                : user
            )
          );

          setSelectedUsers([]);
          toast.success(`Users ${action}d successfully`);
// Audit log: bulk activate/deactivate
for (const uid of usersToUpdate) {
  const userObj = users.find(u => u.id === uid);
  await logUserActivity(
    currentUser?.uid || 'unknown',
    userName || currentUser?.displayName || currentUser?.email || currentUser?.uid || 'unknown',
    `${action.toUpperCase()}_USER_BULK`,
    { targetUserId: uid, targetUserName: userObj?.name || 'unknown' }
  );
}
        } catch (error) {
          console.error(`Error ${action}ing users:`, error);
          toast.error(`Failed to ${action} users`);
        } finally {
          setActionInProgress(false);
        }
      }
    );
  };

  const handleDeactivateUser = async (userId) => {
    if (actionInProgress) return;
    if (userId === currentUser.uid) {
      toast.error('You cannot deactivate your own account');
      return;
    }

    const user = users.find(u => u.id === userId);
    openConfirmModal(
      'Deactivate User',
      `Are you sure you want to deactivate ${user?.name || 'this user'}? They will no longer be able to access the system.`,
      async () => {
        setActionInProgress(true);
        try {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, { active: false });

          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === userId ? { ...user, active: false } : user
            )
          );

          toast.success('User deactivated successfully');
// Audit log: single deactivate
await logUserActivity(
  currentUser?.uid || 'unknown',
  userName || currentUser?.displayName || currentUser?.email || currentUser?.uid || 'unknown',
  'DEACTIVATE_USER',
  { targetUserId: userId, targetUserName: user?.name || 'unknown' }
);
        } catch (error) {
          console.error('Error deactivating user:', error);
          toast.error('Failed to deactivate user');
        } finally {
          setActionInProgress(false);
        }
      }
    );
  };

  const handleActivateUser = async (userId) => {
    if (actionInProgress) return;

    const user = users.find(u => u.id === userId);
    openConfirmModal(
      'Activate User',
      `Are you sure you want to activate ${user?.name || 'this user'}? They will regain access to the system.`,
      async () => {
        setActionInProgress(true);
        try {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, { active: true });

          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === userId ? { ...user, active: true } : user
            )
          );

          toast.success('User activated successfully');
// Audit log: single activate
await logUserActivity(
  currentUser?.uid || 'unknown',
  userName || currentUser?.displayName || currentUser?.email || currentUser?.uid || 'unknown',
  'ACTIVATE_USER',
  { targetUserId: userId, targetUserName: user?.name || 'unknown' }
);
        } catch (error) {
          console.error('Error activating user:', error);
          toast.error('Failed to activate user');
        } finally {
          setActionInProgress(false);
        }
      }
    );
  };

  const handleUpdateTrainingHours = async (userId, requiredHours) => {
    if (actionInProgress) return;

    const user = users.find(u => u.id === userId);
    openConfirmModal(
      'Update Training Hours',
      `Are you sure you want to update ${user?.name || 'this user'}'s required training hours to ${requiredHours}?`,
      async () => {
        setActionInProgress(true);
        try {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, { requiredTrainingHours: parseInt(requiredHours, 10) });
// Audit log: training hours update
await logUserActivity(
  currentUser?.uid || 'unknown',
  userName || currentUser?.displayName || currentUser?.email || currentUser?.uid || 'unknown',
  'UPDATE_TRAINING_HOURS',
  { targetUserId: userId, requiredHours: parseInt(requiredHours, 10) }
);

          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === userId
                ? { ...user, requiredTrainingHours: parseInt(requiredHours, 10) }
                : user
            )
          );

          toast.success('Training hours updated successfully');
        } catch (error) {
          console.error('Error updating training hours:', error);
          toast.error('Failed to update training hours');
        } finally {
          setActionInProgress(false);
        }
      }
    );

    // Ensure modal is visible after it opens
    setTimeout(() => {
      if (modalRef.current) {
        modalRef.current.focus();
        modalRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        // Add a class to highlight the modal
        modalRef.current.classList.add('ring-4', 'ring-accent-light', 'dark:ring-accent-dark');
        // Remove highlight after animation
        setTimeout(() => {
          if (modalRef.current) {
            modalRef.current.classList.remove('ring-4', 'ring-accent-light', 'dark:ring-accent-dark');
          }
        }, 1000);
      }
    }, 100);
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <FiChevronUp className="inline ml-1" /> : <FiChevronDown className="inline ml-1" />;
  };

  // Add these computed values for bulk actions
  const selectedActiveUsers = useMemo(() => {
    return selectedUsers.filter(userId => 
      users.find(user => user.id === userId)?.active
    );
  }, [selectedUsers, users]);

  const selectedInactiveUsers = useMemo(() => {
    return selectedUsers.filter(userId => 
      !users.find(user => user.id === userId)?.active
    );
  }, [selectedUsers, users]);

  return (
    <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark shadow-lg">
      <h2 className="text-sm sm:text-base font-bold mb-3 flex items-center">
        <FiUserCheck className="mr-2 w-4 h-4" />
        User Management
      </h2>

      {/* Search and Filter Section */}
      <div className="flex flex-col gap-2 mb-3">
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-2 py-1.5 text-xs sm:text-sm rounded-md bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:outline-none focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark"
          />
          <FiSearch className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 sm:w-4 sm:h-4" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="flex-grow sm:flex-grow-0 px-2 py-1.5 text-xs sm:text-sm rounded-md bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:outline-none focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="trainer">Trainer</option>
            <option value="trainee">Trainee</option>
          </select>
          {selectedInactiveUsers.length > 0 && (
            <button
              onClick={() => handleBulkAction('activate')}
              disabled={actionInProgress}
              className="flex-grow sm:flex-grow-0 px-2 py-1.5 text-xs sm:text-sm rounded-md bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
            >
              <FiUserCheck className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Activate Selected ({selectedInactiveUsers.length})</span>
            </button>
          )}
          {selectedActiveUsers.length > 0 && (
            <button
              onClick={() => handleBulkAction('deactivate')}
              disabled={actionInProgress}
              className="flex-grow sm:flex-grow-0 px-2 py-1.5 text-xs sm:text-sm rounded-md bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
            >
              <FiUserX className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Deactivate Selected ({selectedActiveUsers.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile View: Card Layout */}
      <div className="block sm:hidden">
        {currentUsers.map((user) => (
          <div key={user.id} className="mb-2 p-2 rounded-lg border border-secondary-light dark:border-secondary-dark">
            <div className="flex items-center justify-between mb-1">
              {user.id !== currentUser.uid && (
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers(prev => [...prev, user.id]);
                    } else {
                      setSelectedUsers(prev => prev.filter(id => id !== user.id));
                    }
                  }}
                  className="w-3 h-3 rounded border-secondary-light dark:border-secondary-dark focus:ring-accent-light dark:focus:ring-accent-dark"
                />
              )}
              {user.id === currentUser.uid && (
                // Spacer for alignment
                <div className="w-3 h-3" />
              )}
              <div className="flex items-center gap-2">
                <UserStatus userId={user.id} />
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  user.active
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100'
                    : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100'
                }`}>
                  {user.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="text-[11px] mb-1">
              <div className="font-semibold">{user.name}</div>
              <div className="text-gray-600 dark:text-gray-400">{user.email}</div>
              <div className="capitalize">{user.role}</div>
            </div>
            {user.role === 'trainee' && (
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="number"
                  value={user.requiredTrainingHours || 0}
                  onChange={(e) => {
                    const newUsers = users.map(u =>
                      u.id === user.id
                        ? { ...u, requiredTrainingHours: parseInt(e.target.value) || 0 }
                        : u
                    );
                    setUsers(newUsers);
                  }}
                  className="w-20 px-2 py-1.5 text-sm rounded bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:outline-none focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark"
                />
                <button
                  onClick={() => handleUpdateTrainingHours(user.id, user.requiredTrainingHours || 0)}
                  className="p-1.5 rounded bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-300 flex items-center justify-center"
                >
                  <FiSave className="w-4 h-4" />
                </button>
              </div>
            )}
            {user.id !== currentUser.uid && (
              <button
                onClick={() => user.active ? handleDeactivateUser(user.id) : handleActivateUser(user.id)}
                className={`w-full px-2 py-2 text-xs rounded-md flex items-center justify-center gap-1.5 ${
                  user.active
                    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100 hover:bg-red-200 dark:hover:bg-red-800'
                    : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800'
                } transition-colors duration-300`}
              >
                {user.active ? (
                  <>
                    <FiUserX className="w-4 h-4" />
                    <span>Deactivate</span>
                  </>
                ) : (
                  <>
                    <FiUserCheck className="w-4 h-4" />
                    <span>Activate</span>
                  </>
                )}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Tablet/Desktop View: Table Layout */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-xs border-b border-secondary-light dark:border-secondary-dark">
              <th className="p-2 text-left">
                <input
                  type="checkbox"
                  checked={currentUsers.length > 0 && selectedUsers.length === currentUsers.filter(user => user.id !== currentUser.uid).length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Select all users except current user
                      setSelectedUsers(currentUsers.filter(user => user.id !== currentUser.uid).map(user => user.id));
                    } else {
                      setSelectedUsers([]);
                    }
                  }}
                  className="w-3 h-3 rounded border-secondary-light dark:border-secondary-dark focus:ring-accent-light dark:focus:ring-accent-dark"
                />
              </th>
              <th className="p-2 text-left cursor-pointer whitespace-nowrap" onClick={() => handleSort('name')}>
                Name <SortIndicator field="name" />
              </th>
              <th className="p-2 text-left cursor-pointer whitespace-nowrap" onClick={() => handleSort('email')}>
                Email <SortIndicator field="email" />
              </th>
              <th className="p-2 text-left cursor-pointer whitespace-nowrap" onClick={() => handleSort('role')}>
                Role <SortIndicator field="role" />
              </th>
              <th className="p-2 text-left cursor-pointer whitespace-nowrap" onClick={() => handleSort('active')}>
                Status <SortIndicator field="active" />
              </th>
              <th className="p-2 text-left whitespace-nowrap">Training Hours</th>
              <th className="p-2 text-left whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => (
              <tr key={user.id} className="border-b border-secondary-light dark:border-secondary-dark hover:bg-secondary-light/10 dark:hover:bg-secondary-dark/10">
                <td className="p-1.5">
                  {user.id !== currentUser.uid ? (
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(prev => [...prev, user.id]);
                        } else {
                          setSelectedUsers(prev => prev.filter(id => id !== user.id));
                        }
                      }}
                      className="w-3 h-3 sm:w-4 sm:h-4 rounded border-secondary-light dark:border-secondary-dark focus:ring-accent-light dark:focus:ring-accent-dark"
                    />
                  ) : (
                    // Spacer for alignment
                    <div className="w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                </td>
                <td className="p-1.5 text-[11px] sm:text-sm">
                  <div className="flex items-center gap-2">
                    {user.name}
                    <UserStatus userId={user.id} />
                  </div>
                </td>
                <td className="p-1.5 text-[11px] sm:text-sm">{user.email}</td>
                <td className="p-1.5 text-[11px] sm:text-sm capitalize">{user.role}</td>
                <td className="p-1.5">
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs ${
                    user.active
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100'
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100'
                  }`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-1.5">
                  {user.role === 'trainee' && (
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="number"
                        value={user.requiredTrainingHours || 0}
                        onChange={(e) => {
                          const newUsers = users.map(u =>
                            u.id === user.id
                              ? { ...u, requiredTrainingHours: parseInt(e.target.value) || 0 }
                              : u
                          );
                          setUsers(newUsers);
                        }}
                        className="w-20 px-2 py-1.5 text-sm rounded bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark focus:outline-none focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark"
                      />
                      <button
                        onClick={() => handleUpdateTrainingHours(user.id, user.requiredTrainingHours || 0)}
                        className="p-1.5 rounded bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-300 flex items-center justify-center"
                      >
                        <FiSave className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
                <td className="p-1.5">
                  {user.id !== currentUser.uid && (
                    <button
                      onClick={() => user.active ? handleDeactivateUser(user.id) : handleActivateUser(user.id)}
                      className={`px-1.5 py-0.5 text-[10px] sm:text-xs rounded-md ${
                        user.active
                          ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100 hover:bg-red-200 dark:hover:bg-red-800'
                          : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800'
                      } transition-colors duration-300`}
                    >
                      {user.active ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex flex-col sm:flex-row justify-between items-center gap-2 text-[11px] sm:text-xs">
        <span className="text-center sm:text-left">
          Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredAndSortedUsers.length)} of {filteredAndSortedUsers.length} users
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 text-[11px] rounded bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-[11px] rounded bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      <ConfirmationModal
        ref={modalRef}
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          confirmModal.onConfirm();
          closeConfirmModal();
        }}
        onClose={closeConfirmModal}
      />
    </div>
  );
};

export default UserManagement;
